import { Capacitor } from "@capacitor/core";
import { StatusBar, Style } from "@capacitor/status-bar";
import { FirebaseMessaging } from "@capacitor-firebase/messaging";
import { LocalNotifications } from "@capacitor/local-notifications";
import { App } from "@capacitor/app";
import { Browser } from "@capacitor/browser";
import { FirebaseAuthentication } from "@capacitor-firebase/authentication";
import { queryClient } from "./queryClient";
import { getNotificationPath } from "./notificationTarget";
import { toast } from "@/hooks/use-toast";
import { captureReferralCode } from "./referral";

function invalidateNotificationQueries(): void {
  queryClient.invalidateQueries({ queryKey: ["/api/my/notifications"] });
  queryClient.invalidateQueries({ queryKey: ["/api/my/notifications/unread-count"] });
  queryClient.invalidateQueries({ queryKey: ["/api/my/orders"] });
  queryClient.invalidateQueries({ queryKey: ["/api/seller/orders"] });
}

// staleTime: Infinity means browse queries never refetch on their own.
// Invalidate them whenever the app comes to foreground so a returning
// user sees newly published listings, sellers, and boxes without
// having to log out and back in.
function invalidateBrowseQueries(): void {
  queryClient.invalidateQueries({ queryKey: ["/api/items"] });
  queryClient.invalidateQueries({ queryKey: ["/api/sellers"] });
  queryClient.invalidateQueries({ queryKey: ["/api/boxes"] });
  queryClient.invalidateQueries({ queryKey: ["/api/my/following-feed"] });
  queryClient.invalidateQueries({ queryKey: ["/api/my/saved-items"] });
}

// Same shape as invalidateBrowseQueries — OrderChat + Messages + the
// header unread badge all poll on 15-30s intervals because there's no
// push channel into the WebView. That polling floor is what users perceive
// as "messages are delayed." Invalidate on foreground and on FCM
// chat_message notifications so refresh feels closer to push.
function invalidateMessageQueries(orderId?: string): void {
  queryClient.invalidateQueries({ queryKey: ["/api/my/conversations"] });
  queryClient.invalidateQueries({ queryKey: ["/api/my/unread-messages"] });
  queryClient.invalidateQueries({ queryKey: ["/api/my/unread-messages-by-order"] });
  if (orderId) {
    queryClient.invalidateQueries({ queryKey: ["/api/orders", orderId, "messages"] });
  } else {
    queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey;
        return (
          Array.isArray(key) &&
          key[0] === "/api/orders" &&
          key[key.length - 1] === "messages"
        );
      },
    });
  }
}

function navigateToNotification(data: Record<string, any> | null | undefined): void {
  const type = typeof data?.type === "string" ? data.type : "";
  const path = getNotificationPath(type, data ?? null) ?? "/notifications";

  // Use the history API + a popstate event so wouter picks up the change
  // without reloading the SPA. Falls back to a full nav if the API isn't there.
  try {
    window.history.pushState(null, "", path);
    window.dispatchEvent(new PopStateEvent("popstate"));
  } catch {
    window.location.href = path;
  }
}

export async function initNativeShell(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;

  // A killed app can leave the delivery background-GPS flag behind; a fresh
  // launch never starts with that engine running, so clear it or the mileage
  // tracker would wrongly avoid its background engine forever.
  try {
    window.localStorage.removeItem("deliveryBgActive");
  } catch { /* ignore */ }

  // Beacon — server log proves initNativeShell ran in the wrapped app's
  // webview. Useful for debugging "is the wrapper actually firing native
  // initialization?" questions.
  void fetch("/api/debug/native-shell-init", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      platform: Capacitor.getPlatform(),
      url: typeof window !== "undefined" ? window.location.href : "",
    }),
  }).catch(() => undefined);

  // Mark the root element so CSS can opt-in to native-only padding/safe areas.
  // This class is never present in a regular browser, so the rules scoped under
  // `html.is-native` have no effect on desktop or mobile web users.
  document.documentElement.classList.add("is-native");
  if (Capacitor.getPlatform() === "android") {
    document.documentElement.classList.add("platform-android");
  }

  try {
    // Let the WebView extend behind the status bar so env(safe-area-inset-top)
    // reports a non-zero value inside CSS. The header element handles the
    // visual offset via padding-top: var(--safe-area-top).
    await StatusBar.setOverlaysWebView({ overlay: true });
    await StatusBar.setBackgroundColor({ color: "#6F8F4F" });
    // Style.Dark = "dark bar" = light/white text+icons. The naming is
    // inverted from intuition: Style.Light gives DARK icons (for light
    // backgrounds) and Style.Dark gives WHITE icons (for dark backgrounds).
    // We want white icons over both the dark-mode background and the
    // light-mode green brand gradient, so Style.Dark is correct globally.
    await StatusBar.setStyle({ style: Style.Dark });
  } catch (error) {
    console.warn("[native-shell] status bar init failed", error);
  }

  void initPushNotifications();
  void initDeepLinks();

  try {
    await App.addListener("appStateChange", ({ isActive }) => {
      if (isActive) {
        invalidateBrowseQueries();
        invalidateMessageQueries();
        invalidateNotificationQueries();
      }
    });
  } catch (error) {
    console.warn("[native-shell] appStateChange listener failed", error);
  }
}

async function initDeepLinks(): Promise<void> {
  try {
    await App.addListener("appUrlOpen", async (event) => {
      const url = event.url;
      if (!url) return;

      const isLegacyOAuth = url.startsWith("marketlink://auth/google");
      const isOAuthCallback =
        /^https:\/\/(trystoop\.com|stoopdrop\.com)\/auth\/google-callback(\?|$)/.test(url);

      if (isLegacyOAuth || isOAuthCallback) {
        // Debug ping so we can see in server logs that the deep link actually
        // reached the wrapped app's listener (vs being swallowed by CCT).
        void fetch("/api/debug/oauth-deeplink-fired", {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ url }),
        }).catch(() => undefined);
        try {
          const parsed = new URL(url);
          const token = parsed.searchParams.get("token");
          const error = parsed.searchParams.get("error");
          if (error) {
            console.warn("[oauth] mobile callback error:", error);
            return;
          }
          if (!token) return;
          const res = await fetch("/api/auth/exchange-token", {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ token }),
          });
          if (!res.ok) {
            console.warn("[oauth] exchange-token returned", res.status);
            return;
          }
          // Don't cache the exchange-token response directly — it's
          // toPublicUser(user) only, which strips server-computed fields
          // like isDemoAdmin and demoAdminCheck. Invalidate so the next
          // useAuth read refetches /api/auth/user and gets the full object.
          await res.json().catch(() => undefined);
          await queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
          await Browser.close().catch(() => undefined);
          try {
            window.history.pushState(null, "", "/");
            window.dispatchEvent(new PopStateEvent("popstate"));
          } catch {
            window.location.href = "/";
          }
        } catch (error) {
          console.warn("[oauth] deep link handler failed", error);
        }
        return;
      }

      // Universal Links / App Links route any non-excluded trystoop.com or
      // stoopdrop.com path into the wrapped app, but the WebView doesn't
      // auto-navigate — we have to push the path ourselves. Without this,
      // tapping a /reset-password link in an email opens the app and drops
      // the user on whatever page was already loaded (typically the splash),
      // which looks like the link did nothing.
      //
      // Only push paths that match a wouter route. Android's assetlinks.json
      // can't filter by path (unlike iOS apple-app-site-association, which
      // excludes /install*, /upgrade*, /privacy*), so appUrlOpen fires for
      // every trystoop.com URL — including the /install referral landing
      // page (server-only route) and any malformed text-message links. If
      // we push an unknown path, wouter falls through to the dev-scaffold
      // NotFound page. Fall back to / for anything we don't recognize.
      const isAppDomain =
        /^https:\/\/(trystoop\.com|stoopdrop\.com)\//.test(url);
      if (!isAppDomain) return;
      try {
        const parsed = new URL(url);
        // /install?ref=X is a referral landing — when an installed user taps
        // it, we fall through to home below, but capture the ref code first
        // so they still get attributed.
        captureReferralCode(parsed.searchParams.get("ref"));
        const pathname = parsed.pathname;
        // Draft post previews are server-rendered HTML, not a wouter route, so
        // pushing them would land on the fallback home page. iOS excludes /p/*
        // in the AASA, but Android's assetlinks.json can't filter by path, so
        // this branch is what stops an installed app from swallowing a preview
        // link sent to a maker.
        if (pathname.startsWith("/p/")) {
          await Browser.open({ url }).catch(() => undefined);
          return;
        }
        const isAppRoute =
          pathname === "/" ||
          /^\/(buy|login|register|forgot-password|reset-password|verify-email|check-email|auth\/google-callback|onboarding|account|seller|checkout|order-confirmation|saved|orders|active|feedback|reservations|reserve|notifications|items|recipes|box-preview|u|sellers|deliveries|messages|privacy|support|terms-of-use|terms|event|garden|upgrade|admin)(\/|$)/.test(pathname);
        const target = isAppRoute ? pathname + parsed.search : "/";
        window.history.pushState(null, "", target);
        window.dispatchEvent(new PopStateEvent("popstate"));
      } catch (error) {
        console.warn("[deeplink] navigation failed", error);
      }
    });
  } catch (error) {
    console.warn("[deeplink] could not register deep link listener", error);
  }
}

/**
 * Sign in with Google using the NATIVE Google Sign-In SDK on iOS/Android.
 *
 * Replaces the previous web-based OAuth flow that tried to bounce back into
 * the wrapped app via Custom Tabs / SafariView + custom URL schemes. That
 * approach hit too many platform-specific pitfalls (Chrome 95+ gesture
 * restrictions, CCT custom-scheme blocking, Universal Link in-tab silent
 * failures, separate cookie stores) to be reliable across devices.
 *
 * Native flow:
 *   1. GoogleAuth.signIn() shows the system Google account picker.
 *   2. Plugin returns an ID token signed by Google for our Web Client ID.
 *   3. We POST the ID token to /api/auth/google/verify-id-token.
 *   4. Server verifies signature + audience, links/creates user, sets
 *      session cookie in the wrapped app's webview.
 *   5. Invalidate /api/auth/user — wouter rerenders Router, isAuthenticated
 *      flips, splash gives way to Home.
 *
 * Web fallback (browser, not wrapped): falls through to the legacy server
 * OAuth redirect flow.
 */
export async function startGoogleOAuthMobile(): Promise<void> {
  if (!Capacitor.isNativePlatform()) {
    window.location.href = "/api/auth/google";
    return;
  }
  const beacon = (stage: string, extra?: Record<string, unknown>) => {
    void fetch("/api/debug/google-signin-beacon", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ stage, platform: Capacitor.getPlatform(), ...extra }),
      keepalive: true,
    }).catch(() => undefined);
  };
  beacon("called");
  try {
    const result = await Promise.race([
      FirebaseAuthentication.signInWithGoogle(),
      new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error("plugin-timeout-15s")), 15000),
      ),
    ]);
    const idToken = (result as any)?.credential?.idToken;
    beacon("plugin-returned", { hasToken: Boolean(idToken), keys: Object.keys((result as any) || {}) });
    if (!idToken) return;
    const res = await fetch("/api/auth/google/verify-id-token", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ idToken }),
    });
    beacon("verify-status", { status: res.status });
    if (!res.ok) {
      const body = await res.json().catch(() => null);
      const message =
        body?.message ||
        (res.status === 409
          ? "This email is already registered. Sign in with your password, then link Google from Account → Security."
          : "Couldn't sign in with Google. Please try again.");
      toast({
        title: "Google sign-in failed",
        description: message,
        variant: "destructive",
      });
      return;
    }
    await res.json().catch(() => undefined);
    await queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
  } catch (error: any) {
    const message = String(error?.message || error);
    beacon("threw", { message: message.slice(0, 300) });
    if (!/cancel|user.cancel|plugin-timeout-15s/i.test(message)) {
      toast({
        title: "Google sign-in failed",
        description: "Something went wrong opening Google. Please try again.",
        variant: "destructive",
      });
    }
  }
}

/**
 * Sign in with Apple, iOS only. Requires the com.apple.developer.applesignin
 * entitlement, so callers must gate on useAppleSignInAvailable() — without it
 * the request fails at runtime inside an older binary.
 *
 * Apple hands back givenName/familyName ONLY on the very first authorization
 * for a given Apple ID. Every later sign-in returns just the opaque subject, so
 * the name is forwarded here and stored at account creation or it is lost for
 * good. Expect @privaterelay.appleid.com addresses when the user hides theirs.
 */
export async function startAppleOAuthMobile(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  try {
    const result = await FirebaseAuthentication.signInWithApple();
    // skipNativeAuth is on, so no Firebase session exists and getIdToken()
    // would return nothing. credential.idToken is Apple's own identity token,
    // which the server verifies against Apple's JWKS. Same shape as Google.
    const idToken = (result as any)?.credential?.idToken;
    if (!idToken) return;
    const displayName = String((result as any)?.user?.displayName || "").trim();
    const res = await fetch("/api/auth/apple/verify-id-token", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        idToken,
        firstName: displayName.split(" ")[0] || null,
        lastName: displayName.split(" ").slice(1).join(" ") || null,
      }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => null);
      toast({
        title: "Apple sign-in failed",
        description:
          body?.message ||
          (res.status === 409
            ? "This email is already registered. Sign in with your password, then link Apple from Account → Security & Account Deletion."
            : "Couldn't sign in with Apple. Please try again."),
        variant: "destructive",
      });
      return;
    }
    await res.json().catch(() => undefined);
    await queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
  } catch (error: any) {
    const message = String(error?.message || error);
    if (!/cancel|user.cancel|1001/i.test(message)) {
      toast({
        title: "Apple sign-in failed",
        description: "Something went wrong opening Apple. Please try again.",
        variant: "destructive",
      });
    }
  }
}

async function initPushNotifications(): Promise<void> {
  try {
    const platform = Capacitor.getPlatform();
    if (platform !== "ios" && platform !== "android") return;

    // Channel ID is suffixed (v2) because Android does not let code raise
    // the importance of an already-created channel — only the user can,
    // via system settings. Bumping the ID forces a fresh IMPORTANCE_HIGH
    // channel for installs that created the original at a lower level.
    await ensureNotificationChannel();

    // Listen before requesting so we don't miss the first token that
    // Firebase mints right after permission is granted.
    FirebaseMessaging.addListener("tokenReceived", (event) => {
      if (event?.token) void postDeviceToken(platform, event.token);
    });

    FirebaseMessaging.addListener("notificationReceived", (event) => {
      invalidateNotificationQueries();
      const n = event?.notification;
      const data = (n?.data as Record<string, any> | undefined) ?? {};
      const type = data.type;
      if (type === "new_listing" || type === "item_saved" || type === "profile_followed") {
        invalidateBrowseQueries();
      }
      if (type === "chat_message") {
        const orderId = typeof data.orderId === "string" ? data.orderId : undefined;
        invalidateMessageQueries(orderId);
      }
      if (type === "support_message") {
        invalidateMessageQueries();
        queryClient.invalidateQueries({ queryKey: ["/api/support-messages/thread"] });
      }
      if (n) {
        void scheduleForegroundLocalNotification({
          title: n.title,
          body: n.body,
          data: n.data,
        });
      }
    });

    FirebaseMessaging.addListener("notificationActionPerformed", (event) => {
      invalidateNotificationQueries();
      const data = (event?.notification?.data as Record<string, any> | undefined) ?? {};
      const type = data.type;
      if (type === "new_listing" || type === "item_saved" || type === "profile_followed") {
        invalidateBrowseQueries();
      }
      if (type === "chat_message") {
        const orderId = typeof data.orderId === "string" ? data.orderId : undefined;
        invalidateMessageQueries(orderId);
      }
      if (type === "support_message") {
        invalidateMessageQueries();
        queryClient.invalidateQueries({ queryKey: ["/api/support-messages/thread"] });
      }
      navigateToNotification(event?.notification?.data as Record<string, any> | null | undefined);
    });

    LocalNotifications.addListener("localNotificationActionPerformed", (action) => {
      invalidateNotificationQueries();
      navigateToNotification(action.notification.extra as Record<string, any>);
    });

    let permissions = await FirebaseMessaging.checkPermissions();
    if (permissions.receive === "prompt" || permissions.receive === "prompt-with-rationale") {
      permissions = await FirebaseMessaging.requestPermissions();
    }
    if (permissions.receive !== "granted") return;

    // getToken() triggers APNs registration on iOS, swizzles in the FCM
    // bridge, and returns an FCM registration token (not the raw APNs
    // hex). That's the token firebase-admin's sendEachForMulticast
    // requires — @capacitor/push-notifications could not provide it.
    try {
      const { token } = await FirebaseMessaging.getToken();
      if (token) await postDeviceToken(platform, token);
    } catch (error) {
      console.warn("[push] getToken failed", error);
    }
  } catch (error) {
    console.warn("[push] init failed", error);
  }
}

async function ensureNotificationChannel(): Promise<void> {
  try {
    if (Capacitor.getPlatform() === "android") {
      await LocalNotifications.createChannel({
        id: "marketlink_alerts_v2",
        name: "Stoop Alerts",
        description: "Orders, messages, deliveries, and feedback",
        importance: 5,
        visibility: 1,
        vibration: true,
        sound: "default",
      });
    }
  } catch {
    /* channel creation failed — notifications still work, just default behavior */
  }
}

async function scheduleForegroundLocalNotification(
  notification: { title?: string; body?: string; data?: any }
): Promise<void> {
  try {
    const permResult = await LocalNotifications.checkPermissions();
    if (permResult.display !== "granted") {
      const requested = await LocalNotifications.requestPermissions();
      if (requested.display !== "granted") return;
    }
    await ensureNotificationChannel();
    const id = Math.floor(Date.now() % 2_000_000_000);
    await LocalNotifications.schedule({
      notifications: [
        {
          id,
          title: notification.title || "Stoop",
          body: notification.body || "",
          extra: notification.data || {},
          channelId: "marketlink_alerts_v2",
          smallIcon: "ic_stat_notification",
        },
      ],
    });
  } catch (error) {
    console.warn("[push] local notification failed", error);
  }
}

let cachedDeviceToken: { platform: "ios" | "android"; token: string } | null = null;

async function postDeviceToken(platform: "ios" | "android", token: string): Promise<void> {
  cachedDeviceToken = { platform, token };
  try {
    const res = await fetch("/api/my/device-tokens", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ platform, token }),
    });
    if (res.ok) {
      cachedDeviceToken = null;
      // Dispatch the same event apiRequest dispatches so the
      // MissionsEngine can auto-complete the "Enable push
      // notifications" mission. This file uses raw fetch (instead of
      // apiRequest) because it boots before the React tree exists,
      // so we mirror the dispatch manually.
      try {
        if (typeof window !== "undefined") {
          window.dispatchEvent(
            new CustomEvent("marketlink:api", {
              detail: { method: "POST", url: "/api/my/device-tokens", status: res.status },
            }),
          );
        }
      } catch {
        /* ignore */
      }
    }
  } catch (error) {
    console.warn("[push] failed to POST device token", error);
  }
}

export async function flushDeviceTokenAfterAuth(): Promise<void> {
  if (!cachedDeviceToken) return;
  const { platform, token } = cachedDeviceToken;
  await postDeviceToken(platform, token);
}
